import type { Provider } from '@angular/core';
import type {
  IBackupRepository,
  IDatabaseAdapter,
  IBackupEncryptionAdapter,
  IBackupQuotaAdapter,
  IBackupRemoteAdapter,
} from '@app/domain/ports';
import {
  LoadBackupsUseCase,
  CreateBackupUseCase,
  RestoreFromBackupUseCase,
  RestoreFromFileUseCase,
  SyncWithRemoteUseCase,
  UploadBackupUseCase,
  DownloadBackupUseCase,
  UpdateBackupPinUseCase,
  ShrinkBackupDataUseCase,
  DeleteBackupUseCase,
  CalculateQuotaUsageUseCase,
  GetLastBackupTimeUseCase,
} from '@app/domain/use-cases';
import {
  BACKUP_REPOSITORY,
  DATABASE_ADAPTER,
  BACKUP_ENCRYPTION_ADAPTER,
  BACKUP_QUOTA_ADAPTER,
  BACKUP_REMOTE_ADAPTER,
} from '../../tokens/infrastructure.tokens';
import { AppStore } from '../../states';

/**
 * Provides Backup domain use cases.
 *
 * Includes:
 * - LoadBackupsUseCase
 * - CreateBackupUseCase
 * - RestoreFromBackupUseCase
 * - RestoreFromFileUseCase
 * - SyncWithRemoteUseCase
 * - UploadBackupUseCase
 * - DownloadBackupUseCase
 * - UpdateBackupPinUseCase
 * - ShrinkBackupDataUseCase
 * - DeleteBackupUseCase
 * - CalculateQuotaUsageUseCase
 * - GetLastBackupTimeUseCase
 */
export function provideBackupDomain(): Provider[] {
  return [
    {
      provide: LoadBackupsUseCase,
      useFactory: (repository: IBackupRepository) => new LoadBackupsUseCase(repository),
      deps: [BACKUP_REPOSITORY],
    },
    {
      provide: CreateBackupUseCase,
      useFactory: (
        repository: IBackupRepository,
        databaseAdapter: IDatabaseAdapter,
        encryptionAdapter: IBackupEncryptionAdapter,
        quotaAdapter: IBackupQuotaAdapter,
        remoteAdapter: IBackupRemoteAdapter,
        appStore: InstanceType<typeof AppStore>,
      ) =>
        new CreateBackupUseCase(
          repository,
          databaseAdapter,
          encryptionAdapter,
          quotaAdapter,
          remoteAdapter,
          () => appStore.encryptData(),
          () => appStore.googleDriveEnabled(),
          () => appStore.maxBackupCount(),
        ),
      deps: [
        BACKUP_REPOSITORY,
        DATABASE_ADAPTER,
        BACKUP_ENCRYPTION_ADAPTER,
        BACKUP_QUOTA_ADAPTER,
        BACKUP_REMOTE_ADAPTER,
        AppStore,
      ],
    },
    {
      provide: RestoreFromBackupUseCase,
      useFactory: (
        repository: IBackupRepository,
        remoteAdapter: IBackupRemoteAdapter,
        encryptionAdapter: IBackupEncryptionAdapter,
        databaseAdapter: IDatabaseAdapter,
      ) => new RestoreFromBackupUseCase(repository, remoteAdapter, encryptionAdapter, databaseAdapter),
      deps: [BACKUP_REPOSITORY, BACKUP_REMOTE_ADAPTER, BACKUP_ENCRYPTION_ADAPTER, DATABASE_ADAPTER],
    },
    {
      provide: RestoreFromFileUseCase,
      useFactory: (databaseAdapter: IDatabaseAdapter, encryptionAdapter: IBackupEncryptionAdapter) =>
        new RestoreFromFileUseCase(databaseAdapter, encryptionAdapter),
      deps: [DATABASE_ADAPTER, BACKUP_ENCRYPTION_ADAPTER],
    },
    {
      provide: SyncWithRemoteUseCase,
      useFactory: (remoteAdapter: IBackupRemoteAdapter) => new SyncWithRemoteUseCase(remoteAdapter),
      deps: [BACKUP_REMOTE_ADAPTER],
    },
    {
      provide: UploadBackupUseCase,
      useFactory: (repository: IBackupRepository, remoteAdapter: IBackupRemoteAdapter) =>
        new UploadBackupUseCase(repository, remoteAdapter),
      deps: [BACKUP_REPOSITORY, BACKUP_REMOTE_ADAPTER],
    },
    {
      provide: DownloadBackupUseCase,
      useFactory: (
        repository: IBackupRepository,
        remoteAdapter: IBackupRemoteAdapter,
        quotaAdapter: IBackupQuotaAdapter,
      ) => new DownloadBackupUseCase(repository, remoteAdapter, quotaAdapter),
      deps: [BACKUP_REPOSITORY, BACKUP_REMOTE_ADAPTER, BACKUP_QUOTA_ADAPTER],
    },
    {
      provide: UpdateBackupPinUseCase,
      useFactory: (
        repository: IBackupRepository,
        remoteAdapter: IBackupRemoteAdapter,
        appStore: InstanceType<typeof AppStore>,
      ) => new UpdateBackupPinUseCase(repository, remoteAdapter, () => appStore.googleDriveEnabled()),
      deps: [BACKUP_REPOSITORY, BACKUP_REMOTE_ADAPTER, AppStore],
    },
    {
      provide: ShrinkBackupDataUseCase,
      useFactory: (repository: IBackupRepository, appStore: InstanceType<typeof AppStore>) =>
        new ShrinkBackupDataUseCase(repository, () => appStore.googleDriveEnabled()),
      deps: [BACKUP_REPOSITORY, AppStore],
    },
    {
      provide: DeleteBackupUseCase,
      useFactory: (
        repository: IBackupRepository,
        remoteAdapter: IBackupRemoteAdapter,
        appStore: InstanceType<typeof AppStore>,
      ) => new DeleteBackupUseCase(repository, remoteAdapter, () => appStore.googleDriveEnabled()),
      deps: [BACKUP_REPOSITORY, BACKUP_REMOTE_ADAPTER, AppStore],
    },
    {
      provide: CalculateQuotaUsageUseCase,
      useFactory: (quotaAdapter: IBackupQuotaAdapter) => new CalculateQuotaUsageUseCase(quotaAdapter),
      deps: [BACKUP_QUOTA_ADAPTER],
    },
    {
      provide: GetLastBackupTimeUseCase,
      useFactory: (repository: IBackupRepository) => new GetLastBackupTimeUseCase(repository),
      deps: [BACKUP_REPOSITORY],
    },
  ];
}
